import { Injectable, inject, NgZone } from "@angular/core";
import {
	Firestore,
	collection,
	doc,
	getDoc,
	setDoc,
	updateDoc,
	query,
	where,
	getDocs,
	serverTimestamp,
} from "@angular/fire/firestore";
import { Observable, from, map, switchMap, of, catchError, throwError } from "rxjs";
import { UserWithRoles, UserRole } from "../models/user-role.model";
import { Organization } from "../models/organization.model";
import { Functions, httpsCallable, getFunctions } from "@angular/fire/functions";
import { Auth, sendEmailVerification, sendPasswordResetEmail } from "@angular/fire/auth";
import { AuthService } from "./auth.service";
import { CacheService, buildCacheKey } from "./cache.service";
import { environment } from "../../environments/environment";
import { fetchDocsByIds } from "../shared/utils/docs-by-id.util";
import { normalizeEmail } from "../shared/utils/email.util";
@Injectable({
	providedIn: "root",
})
export class UserService {
	private firestore: Firestore = inject(Firestore);
	private functions: Functions = inject(Functions);
	private zone: NgZone = inject(NgZone);
	private authService: AuthService = inject(AuthService);
	private auth: Auth = inject(Auth);
	private cacheService: CacheService = inject(CacheService);

	constructor() {}

	// Get all users
	getUsers(): Observable<UserWithRoles[]> {
		const usersCollection = collection(this.firestore, "users");
		const q = environment.production
			? query(usersCollection, where("active", "!=", false))
			: query(usersCollection);

		return from(getDocs(q)).pipe(
			map((snapshot) =>
				snapshot.docs.map(
					(doc) =>
						({
							...doc.data(),
							uid: doc.id,
						}) as UserWithRoles,
				),
			),
		);
	}

	getUserExists(email: string): Observable<boolean> {
		const usersCollection = collection(this.firestore, "users");
		const q = query(usersCollection, where("email", "==", normalizeEmail(email)));
		return from(getDocs(q)).pipe(map((snapshot) => !snapshot.empty));
	}

	// Get a single user by ID
	getUser(uid: string): Observable<UserWithRoles | null> {
		return new Observable<UserWithRoles | null>((observer) => {
			this.zone.runOutsideAngular(() => {
				const userDocRef = doc(this.firestore, `users/${uid}`);
				getDoc(userDocRef)
					.then((docSnap) => {
						this.zone.run(() => {
							if (docSnap.exists()) {
								const data = docSnap.data();
								// In production, return null for inactive users
								if (environment.production && data["active"] === false) {
									observer.next(null);
								} else {
									observer.next({
										uid: docSnap.id,
										email: data["email"] || "",
										photo_url: data["photo_url"],
										roles: data["roles"] || {},
										active: data["active"],
										first_name: data["first_name"] || "",
										last_name: data["last_name"] || "",
									});
								}
							} else {
								observer.next(null);
							}
							observer.complete();
						});
					})
					.catch((error) => {
						this.zone.run(() => {
							observer.error(error);
						});
					});
			});
		});
	}

	// Update a user
	updateUser(uid: string, userData: Partial<UserWithRoles>): Observable<void> {
		return new Observable<void>((observer) => {
			this.zone.runOutsideAngular(() => {
				const userDocRef = doc(this.firestore, `users/${uid}`);
				updateDoc(userDocRef, {
					...userData,
					updated_at: new Date(),
				})
					.then(() => {
						this.zone.run(() => {
							this.clearUserCaches(uid);
							observer.next();
							observer.complete();
						});
					})
					.catch((error) => {
						this.zone.run(() => {
							observer.error(error);
						});
					});
			});
		});
	}

	// Assign a user to an organization
	assignUserToOrganization(
		uid: string,
		organizationId: string,
		role: string = "respondent",
	): Observable<void> {
		return this.authService.user$.pipe(
			switchMap((user) => {
				if (!user) {
					throw new Error("User not authenticated");
				}

				const associationId = `${organizationId}_${uid}`;
				const associationRef = doc(this.firestore, "organizationUsers", associationId);

				const associationData = {
					active: true,
					user_id: uid,
					organization_id: organizationId,
					roles: { [role]: true },
					created_at: new Date(),
					updated_at: new Date(),
				};

				return from(setDoc(associationRef, associationData)).pipe(
					map(() => this.clearMembershipCaches(uid)),
				);
			}),
		);
	}

	// Remove a user from an organization
	removeUserFromOrganization(uid: string, organizationId: string): Observable<void> {
		const associationId = `${organizationId}_${uid}`;
		const associationRef = doc(this.firestore, "organizationUsers", associationId);
		// only update the active field to false
		return from(updateDoc(associationRef, { active: false })).pipe(
			map(() => this.clearMembershipCaches(uid)),
		);
	}

	// Get the organization a user belongs to
	getUserOrganization(uid: string): Observable<Organization | null> {
		const associationsRef = collection(this.firestore, "organizationUsers");
		const q = query(associationsRef, where("user_id", "==", uid), where("active", "==", true));

		return from(getDocs(q)).pipe(
			switchMap((snapshot) => {
				if (snapshot.empty) return of(null);
				const organizationId = snapshot.docs[0].data()["organization_id"];

				// Fetch organization document
				return from(getDoc(doc(this.firestore, "organizations", organizationId))).pipe(
					map((orgDoc) =>
						orgDoc.exists()
							? ({ id: orgDoc.id, ...orgDoc.data() } as Organization)
							: null,
					),
				);
			}),
		);
	}

	// Get all users in an organization
	getUsersInOrganization(organizationId: string): Observable<UserWithRoles[]> {
		const associationsRef = collection(this.firestore, "organizationUsers");
		const q = query(
			associationsRef,
			where("organization_id", "==", organizationId),
			where("active", "==", true),
		);

		return from(getDocs(q)).pipe(
			switchMap((snapshot) => {
				const userIds = snapshot.docs.map((doc) => doc.data()["user_id"]);
				if (userIds.length === 0) return from([]);

				// Fetch the user documents in batches
				return from(
					fetchDocsByIds(this.firestore, "users", userIds).then((docs) =>
						Array.from(docs)
							// In production, skip inactive users
							.filter(
								([, userData]) =>
									!(environment.production && userData["active"] === false),
							)
							.map(
								([id, userData]) =>
									({
										id,
										email: userData["email"] || "",
										first_name: userData["first_name"] || "",
										last_name: userData["last_name"] || "",
										roles: userData["roles"] || {},
										active: userData["active"],
										...userData,
									}) as UserWithRoles,
							),
					),
				);
			}),
		);
	}

	// Track pending user creation requests to prevent duplicates
	private pendingUserCreations = new Map<string, boolean>();

	// Create a new user.
	//
	// sendWelcomeEmail is required rather than defaulted (issue #64): the
	// backend rejects the call without it, so every caller has to decide.
	// Respondent adds pass false; back-office user creation passes true,
	// since the welcome link is that user's only way in.
	createUser(
		userData: any,
		addFrom: "global" | "organizations",
		sendWelcomeEmail: boolean,
	): Observable<string> {
		const email = userData.email ? String(userData.email).toLowerCase().trim() : "";

		if (!email) {
			return throwError(() => new Error("Email is required"));
		}

		// Check if we're already creating a user with this email to prevent duplicate calls
		if (this.pendingUserCreations.has(email)) {
			return throwError(() => ({
				code: "in-progress",
				message:
					"User creation is already in progress for this email. Please wait a moment.",
			}));
		}

		// Set a flag to track that this email is being processed
		this.pendingUserCreations.set(email, true);

		// Handle roles from the form
		let rolesObject: Record<string, boolean> = {};
		if (userData.roles && typeof userData.roles === "object") {
			// Direct roles object from the form
			rolesObject = userData.roles;
		} else if (userData.role) {
			// Legacy single role string format
			const isGlobalAdmin = userData.role === UserRole.ADMIN;

			if (isGlobalAdmin) {
				rolesObject[UserRole.ADMIN] = true;
			} else {
				rolesObject[userData.role] = true;
			}
		}

		// Prepare data for user creation - use snake_case for cloud function
		const userCreateData = {
			email: email,
			roles: rolesObject,
			organization_id: userData.organization_id || null,
			first_name: userData.first_name || "",
			last_name: userData.last_name || "",
			send_welcome_email: sendWelcomeEmail,
		};

		// Get the functions instance with the correct region
		const functionsWithRegion = getFunctions(undefined, "europe-west1");

		// Call the Cloud Function to create the user in both Auth and Firestore
		const createUserFn = httpsCallable(functionsWithRegion, "userCreate");

		// Pass the data directly without wrapping it
		return from(createUserFn(userCreateData)).pipe(
			map((result) => {
				// Clear the pending flag after success
				this.pendingUserCreations.delete(email);
				// The new user has to show up in every cached user list
				this.cacheService.clearPattern("users");
				this.cacheService.clearPattern("organizations");
				this.cacheService.clearPattern("dashboard");

				// userCreate resolves with { uid, sign_in_link_sent }. This used to
				// cast that object straight to string, so callers interpolated
				// "[object Object]" into an association id and wrote the whole
				// object under user_id — one such row was enough to make an
				// organisation's respondent list throw and render empty.
				const payload = result.data as { uid?: unknown };
				if (typeof payload?.uid !== "string" || !payload.uid) {
					throw new Error("userCreate returned no uid");
				}
				return payload.uid;
			}),
			catchError((error) => {
				console.error("Error creating user:", error.message || error);

				// Clear the pending flag after error
				this.pendingUserCreations.delete(email);

				// Handle specific error codes from the cloud function
				if (
					error.code === "already-exists" ||
					(error.details && error.details.code === "already-exists") ||
					error.message?.includes("already in use")
				) {
					return throwError(() => ({
						code: "already-exists",
						message:
							"The email address is already in use by another account. Please use a different email address or ask the user to sign in with their existing account.",
					}));
				}

				// Special handling for potential successful user creation despite error
				if (error.message === "INTERNAL" || error.code === "internal") {
					return from(this.checkIfUserExistsByEmail(userCreateData.email)).pipe(
						switchMap((exists) => {
							if (exists) {
								return of("PARTIAL_SUCCESS");
							} else {
								return throwError(() => error);
							}
						}),
						catchError(() => {
							return throwError(() => error);
						}),
					);
				}

				return throwError(() => error);
			}),
		);
	}

	// Helper method to check if user exists by email
	private checkIfUserExistsByEmail(email: string): Promise<boolean> {
		// Add a small delay to allow Firestore to catch up with the new user
		return new Promise((resolve) => {
			// Wait for 2 seconds to allow Firestore to update
			setTimeout(() => {
				getDocs(
					query(
						collection(this.firestore, "users"),
						where("email", "==", normalizeEmail(email)),
					),
				)
					.then((snapshot) => {
						const exists = !snapshot.empty;

						if (exists) {
							resolve(true);
						} else {
							// If not found, try again after a short delay
							setTimeout(() => {
								getDocs(
									query(
										collection(this.firestore, "users"),
										where("email", "==", normalizeEmail(email)),
									),
								)
									.then((secondSnapshot) => {
										const existsOnSecondTry = !secondSnapshot.empty;
										resolve(existsOnSecondTry);
									})
									.catch((error) => {
										console.error(
											"Error on second attempt checking if user exists:",
											error,
										);
										// If even the second check fails, assume user was created
										resolve(true);
									});
							}, 2000);
						}
					})
					.catch((error) => {
						console.error("Error checking if user exists:", error);
						// If we can't check, assume user was created to be safe
						resolve(true);
					});
			}, 2000);
		});
	}

	// Delete a user
	deleteUser(uid: string | undefined, hardDelete: boolean = false): Observable<void> {
		if (!uid) {
			return throwError(() => new Error("User ID is required"));
		}

		// In production, or if hardDelete is not requested, mark as inactive
		if (environment.production || !hardDelete) {
			const userRef = doc(this.firestore, "users", uid);
			return from(
				updateDoc(userRef, {
					active: false,
					updated_at: serverTimestamp(),
				}),
			).pipe(map(() => this.clearUserCaches(uid)));
		}

		// Only allow hard delete in non-production when specifically requested
		return new Observable<void>((observer) => {
			try {
				// Get the functions instance with the correct region
				const functionsWithRegion = getFunctions(undefined, "europe-west1");

				// Call the Cloud Function to delete the user from both Auth and Firestore
				const deleteUserFn = httpsCallable(functionsWithRegion, "userDelete");

				// Pass the user ID to the Cloud Function
				deleteUserFn({ uid })
					.then(() => {
						this.clearUserCaches(uid);
						observer.next();
						observer.complete();
					})
					.catch((error) => {
						console.error("Error deleting user:", error.message || error);
						observer.error(error);
					});
			} catch (error) {
				console.error("Exception during user deletion:", error);
				observer.error(error);
			}
		});
	}

	// Drop every cached view that a write to this user's document can invalidate
	private clearUserCaches(uid: string): void {
		this.cacheService.remove(buildCacheKey("users", "single", uid));
		this.cacheService.clearPattern("users");
		this.cacheService.clearPattern("dashboard");
	}

	// Membership changes also move the user between organization-scoped lists
	private clearMembershipCaches(uid: string): void {
		this.clearUserCaches(uid);
		this.cacheService.clearPattern("organizations");
	}

	// Update a user's email using the Cloud Function
	updateUserEmail(uid: string, email: string): Observable<any> {
		return new Observable((observer) => {
			try {
				// Create a simple plain object with primitive values
				const plainData = {
					uid: String(uid),
					email: String(email),
				};

				// Get the functions instance with the correct region
				const functionsWithRegion = getFunctions(undefined, "europe-west1");

				// Get the function reference
				const updateEmailFn = httpsCallable(functionsWithRegion, "userEmailUpdate");

				// Call the function with the plain data
				updateEmailFn(plainData)
					.then((result) => {
						this.clearUserCaches(uid);
						observer.next(result);
						observer.complete();
					})
					.catch((error) => {
						console.error("Error updating user email:", error);
						observer.error(error);
					});
			} catch (error) {
				console.error("Exception in updateUserEmail:", error);
				observer.error(error);
			}
		});
	}

	// Reset a user's password using the Cloud Function
	resetUserPassword(uid: string): Observable<any> {
		return new Observable((observer) => {
			try {
				// Create a simple plain object with primitive values
				const plainData = {
					uid: String(uid),
				};

				// Get the functions instance with the correct region
				const functionsWithRegion = getFunctions(undefined, "europe-west1");

				// Get the function reference
				const resetPasswordFn = httpsCallable(functionsWithRegion, "userPasswordReset");

				// Call the function with the plain data
				resetPasswordFn(plainData)
					.then((result) => {
						observer.next(result);
						observer.complete();
					})
					.catch((error) => {
						console.error("Error resetting user password:", error);
						observer.error(error);
					});
			} catch (error) {
				console.error("Exception in resetUserPassword:", error);
				observer.error(error);
			}
		});
	}

	// Helper method to get a user's UID from their email
	private getUidFromEmail(email: string): Observable<string | null> {
		const usersRef = collection(this.firestore, "users");
		const q = query(usersRef, where("email", "==", normalizeEmail(email)));

		return from(getDocs(q)).pipe(
			map((querySnapshot) => {
				if (querySnapshot.empty) {
					return null;
				}
				const userDoc = querySnapshot.docs[0];
				return userDoc.id;
			}),
			catchError((error) => {
				console.error(`Error looking up user by email ${email}:`, error);
				return of(null);
			}),
		);
	}

	// Send a verification email
	sendVerificationEmail(email: string): Observable<boolean> {
		// First check if the user exists
		const usersRef = collection(this.firestore, "users");
		const q = query(usersRef, where("email", "==", normalizeEmail(email)));

		return from(getDocs(q)).pipe(
			switchMap((querySnapshot) => {
				if (querySnapshot.empty) {
					console.error(
						`Cannot send verification email: User with email ${email} not found`,
					);
					return throwError(() => new Error(`User with email ${email} not found`));
				}

				const userDoc = querySnapshot.docs[0];
				const userData = userDoc.data() as UserWithRoles;
				const uid = userDoc.id;

				// Check if the user is verified already.
				//
				// This read `userData.emailVerified` — a field the document does
				// not have, since Firestore stores `email_verified` — so it was
				// always undefined and the short-circuit never fired.
				if (userData.email_verified) {
					// Already verified, nothing to do
					return of(true);
				}

				const currentUser = this.auth.currentUser;

				// We need to be signed in as the user to send them a verification email
				if (!currentUser || currentUser.uid !== uid) {
					console.error(
						`Cannot send verification email: Current user (${currentUser?.uid}) is not the target user (${uid})`,
					);
					return throwError(
						() => new Error("You must be signed in as the user to verify their email"),
					);
				}

				const actionCodeSettings = {
					url: `${window.location.origin}/dashboard`,
					handle_code_in_app: true,
				};

				return from(sendEmailVerification(currentUser, actionCodeSettings)).pipe(
					map(() => true),
					catchError((error) => {
						console.error(`Error sending verification email to ${email}:`, error);
						return throwError(() => error);
					}),
				);
			}),
			catchError((error) => {
				console.error(`Error in verification email process for ${email}:`, error);
				return throwError(() => error);
			}),
		);
	}

	// Send a verification email to the currently signed-in user
	sendVerificationEmailToCurrentUser(): Observable<void> {
		return new Observable((observer) => {
			void this.zone.runOutsideAngular(async () => {
				try {
					const currentUser = this.auth.currentUser;

					if (!currentUser) {
						this.zone.run(() => {
							console.error("No user is currently signed in");
							observer.error(new Error("No user is currently signed in"));
						});
						return;
					}

					// Reload the user to get the latest emailVerified status
					await currentUser.reload();

					// Check if the email is already verified
					if (currentUser.emailVerified) {
						this.zone.run(() => {
							observer.next();
							observer.complete();
						});
						return;
					}

					try {
						// Send the verification email
						await sendEmailVerification(currentUser);

						this.zone.run(() => {
							observer.next();
							observer.complete();
						});
					} catch (error) {
						this.zone.run(() => {
							console.error("Error sending verification email:", error);
							observer.error(error);
						});
					}
				} catch (error) {
					this.zone.run(() => {
						console.error("Exception in sendVerificationEmailToCurrentUser:", error);
						observer.error(error);
					});
				}
			});
		});
	}

	// Check if a user's email is verified and send a verification email if it's not
	// This should be called after a user signs in
	checkAndSendVerificationEmail(): Observable<boolean> {
		const currentUser = this.auth.currentUser;

		if (!currentUser) {
			console.error("Cannot send verification email: No user is currently signed in");
			return throwError(() => new Error("No user is currently signed in"));
		}

		if (currentUser.emailVerified) {
			// Email is already verified, nothing to do
			return of(true);
		}

		const actionCodeSettings = {
			url: `${window.location.origin}/dashboard`,
			handle_code_in_app: true,
		};

		return from(sendEmailVerification(currentUser, actionCodeSettings)).pipe(
			map(() => true),
			catchError((error) => {
				console.error(
					`Error sending verification email to current user (${currentUser.email}):`,
					error,
				);
				return throwError(() => error);
			}),
		);
	}

	// Manually update the lastSignInAt field for a user
	updateUserLastSignIn(uid: string): Observable<void> {
		return new Observable((observer) => {
			void this.zone.runOutsideAngular(async () => {
				try {
					// Get the user document reference
					const userRef = doc(this.firestore, `users/${uid}`);

					// Update the document with the current server timestamp
					await updateDoc(userRef, {
						last_sign_in_at: serverTimestamp(),
					});

					this.zone.run(() => {
						observer.next();
						observer.complete();
					});
				} catch (error) {
					this.zone.run(() => {
						console.error(`Error updating lastSignInAt for user:`, error);
						observer.error(error);
					});
				}
			});
		});
	}

	// Reset a user's password directly using Firebase's client-side SDK
	resetUserPasswordDirectly(email: string): Observable<void> {
		const actionCodeSettings = {
			url: `${window.location.origin}/verify-email?email=${encodeURIComponent(email)}`,
			handle_code_in_app: true,
		};

		return from(sendPasswordResetEmail(this.auth, email, actionCodeSettings)).pipe(
			catchError((error) => {
				console.error(`Error sending password reset email to ${email}:`, error);
				return throwError(() => error);
			}),
		);
	}

	// Get all organizations a user has access to
	getUserOrganizations(userId: string): Observable<Organization[]> {
		const associationsRef = collection(this.firestore, "organizationUsers");
		const q = query(
			associationsRef,
			where("user_id", "==", userId),
			where("active", "==", true),
		);

		return from(getDocs(q)).pipe(
			switchMap((snapshot) => {
				if (snapshot.empty) return of([]);

				const organizationIds = snapshot.docs.map((doc) => doc.data()["organization_id"]);

				// Fetch the organization documents in batches
				return from(
					fetchDocsByIds(this.firestore, "organizations", organizationIds).then((docs) =>
						Array.from(docs)
							.filter(([, data]) => data["active"])
							.map(([id, data]) => ({ id, ...data }) as Organization),
					),
				);
			}),
		);
	}
}
